import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search, RefreshCw, AlertCircle, AlertTriangle, CheckCircle,
  PenLine, ExternalLink, ChevronDown, ChevronUp, ListChecks,
  TrendingUp, X, Loader2, Sparkles,
} from "lucide-react";
import * as api from "../lib/api";

const PERIOD_OPTIONS = [
  { value: 28, label: "28 days" },
  { value: 90, label: "3 months" },
  { value: 180, label: "6 months" },
];

const PRIMARY = "#018D4C";

const INTENT_COLORS = {
  informational: { bg: "#EFF6FF", text: "#1D4ED8" },
  commercial:    { bg: "#FFFBEB", text: "#B45309" },
  transactional: { bg: "#ECFDF5", text: "#065F46" },
  comparison:    { bg: "#F5F3FF", text: "#6D28D9" },
  problem_aware: { bg: "#FFF1F2", text: "#BE123C" },
};

const ALL_INTENTS = ["informational", "commercial", "transactional", "comparison", "problem_aware"];

function IntentBadge({ intent }) {
  const c = INTENT_COLORS[intent] || { bg: "#F4F4F5", text: "#52525B" };
  return (
    <span
      className="px-2 py-0.5 text-[9px] tracking-[0.12em] font-bold uppercase rounded-full"
      style={{ background: c.bg, color: c.text }}
    >
      {intent?.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-4">
      <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-zinc-900">{value ?? "—"}</p>
      {sub && <p className="text-[10px] text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function CtrFixModal({ query, onClose }) {
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editMeta, setEditMeta] = useState("");

  useEffect(() => {
    api.suggestCtrFix(query.id)
      .then(r => {
        setSuggestion(r);
        setEditTitle(r.suggested_title || "");
        setEditMeta(r.suggested_meta || "");
      })
      .catch(() => setError("Failed to generate suggestions."))
      .finally(() => setLoading(false));
  }, [query.id]);

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      await api.applyCtrFix(query.id, { title: editTitle || undefined, meta_desc: editMeta || undefined });
      setApplied(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to apply changes.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">CTR Optimization</p>
            <p className="text-sm font-semibold text-zinc-900 mt-0.5 truncate max-w-xs">{query.query}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 p-1"><X size={16} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-zinc-400 text-xs py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Generating suggestions…
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center gap-2 text-red-600 text-xs">
              <AlertCircle size={13} /> {error}
            </div>
          )}
          {!loading && suggestion && !applied && (
            <>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Page Title</p>
                <p className="text-[10px] text-zinc-400 mb-1">Current: <span className="text-zinc-600">{suggestion.current_title}</span></p>
                <textarea
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  rows={2}
                  className="w-full text-xs border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
                <p className={`text-[10px] mt-0.5 text-right ${editTitle.length > 60 ? "text-orange-500" : "text-zinc-400"}`}>
                  {editTitle.length} / 60 chars
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Meta Description</p>
                <textarea
                  value={editMeta}
                  onChange={e => setEditMeta(e.target.value)}
                  rows={3}
                  className="w-full text-xs border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
                <p className={`text-[10px] mt-0.5 text-right ${editMeta.length > 155 ? "text-orange-500" : editMeta.length >= 120 ? "text-green-600" : "text-zinc-400"}`}>
                  {editMeta.length} / 155 chars
                </p>
              </div>
            </>
          )}
          {applied && (
            <div className="flex items-center gap-2 text-green-700 text-xs py-4 justify-center">
              <CheckCircle size={16} /> Changes applied.
            </div>
          )}
        </div>

        {!loading && suggestion && !applied && (
          <div className="flex items-center justify-end gap-2 px-5 pb-4">
            <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-zinc-500 hover:text-zinc-900">
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={applying || (!editTitle && !editMeta)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              {applying ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
              {applying ? "Applying…" : "Apply to site"}
            </button>
          </div>
        )}
        {applied && (
          <div className="flex justify-end px-5 pb-4">
            <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-white rounded-lg" style={{ backgroundColor: PRIMARY }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function QueryCard({ query, onWrite, onFaq, onFixCtr }) {
  const [expanded, setExpanded] = useState(false);
  const [optimizeState, setOptimizeState] = useState({ status: "idle" });
  const pollRef = useRef(null);
  const isOrphan = query.gap_type === "orphan";
  const fit = query.page_fit;
  const isCannibalization = fit === "cannibalization";
  const isWrongPage = fit === "wrong_page";

  const handleAutoOptimize = async (e) => {
    e.stopPropagation();
    if (optimizeState.status === "running") return;
    setOptimizeState({ status: "running", step: "starting" });
    try {
      const res = await api.autoOptimizeQuery(query.id);
      setOptimizeState(res);
      if (res.status === "running") {
        pollRef.current = setInterval(async () => {
          try {
            const s = await api.getOptimizeStatus(query.id);
            setOptimizeState(s);
            if (s.status !== "running") clearInterval(pollRef.current);
          } catch {}
        }, 3000);
      }
    } catch {
      setOptimizeState({ status: "failed", error: "Failed to start." });
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const optimizeIcon = () => {
    if (optimizeState.status === "running") return <Loader2 size={11} className="animate-spin" />;
    if (optimizeState.status === "done") return <CheckCircle size={11} className="text-green-600" />;
    if (optimizeState.status === "failed") return <AlertCircle size={11} className="text-red-500" />;
    return <Sparkles size={11} strokeWidth={2.5} />;
  };

  const optimizeLabel = () => {
    if (optimizeState.status === "running") return optimizeState.step || "Running…";
    if (optimizeState.status === "done") return optimizeState.wp_applied ? "Applied" : "Done";
    if (optimizeState.status === "failed") return "Failed";
    return "Optimize";
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/dashboard/query-gaps/${query.id}`}
            className="text-sm font-semibold text-zinc-900 leading-snug hover:text-green-700 transition-colors"
          >
            {query.query}
          </Link>
          {query.article_title && (
            <p className="text-[10px] text-zinc-500 mt-0.5 truncate">
              Page: <span className="font-medium text-zinc-700">{query.article_title}</span>
            </p>
          )}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <IntentBadge intent={query.intent} />
            {!isOrphan && query.position != null && (
              <span className="text-[10px] text-zinc-500">
                Pos <span className="font-bold text-orange-600">{Number(query.position).toFixed(1)}</span>
              </span>
            )}
            <span className="text-[10px] text-zinc-500">
              <span className="font-bold text-zinc-700">{query.impressions?.toLocaleString()}</span> impr
            </span>
            <span className="text-[10px] text-zinc-500">
              <span className="font-bold text-zinc-700">{query.clicks?.toLocaleString()}</span> clicks
            </span>
            {query.ctr > 0 && (
              <span className="text-[10px] text-zinc-500">
                CTR <span className="font-bold text-zinc-700">{(query.ctr * 100).toFixed(1)}%</span>
              </span>
            )}
          </div>
          {expanded && query.reasoning && (
            <p className="text-[11px] text-zinc-500 mt-2 italic">{query.reasoning}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {query.reasoning && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-zinc-400 hover:text-zinc-700 p-1"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
          {/* page_fit gates which action makes sense — rewriting title/meta on a page
              that's fundamentally the wrong page, or one of several competing pages,
              doesn't fix the underlying problem, so those two fits get a different
              (or no) action instead of the blind CTR-rewrite Optimize/Fix CTR flow. */}
          {!isOrphan && isCannibalization && (
            <span
              title="Multiple pages rank for this query — merge/redirect into one canonical page instead of editing title/meta on just this one."
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border border-purple-200 bg-purple-50 text-purple-700 rounded-lg"
            >
              <AlertTriangle size={11} strokeWidth={2.5} />
              Cannibalization
            </span>
          )}
          {!isOrphan && isWrongPage && (
            <button
              onClick={() => onWrite(query, false)}
              title="This page doesn't match the query's intent — a dedicated page is a better fix than editing this one's title/meta."
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              <PenLine size={11} strokeWidth={2.5} />
              Write Dedicated Page
            </button>
          )}
          {/* Auto-optimize button (underperforming, good/weak/unclassified fit only) */}
          {!isOrphan && !isCannibalization && !isWrongPage && (
            <button
              onClick={handleAutoOptimize}
              disabled={optimizeState.status === "running"}
              title="Auto-crawl page, generate AI copy improvements, and apply to site"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border rounded-lg transition-colors disabled:opacity-60 ${
                optimizeState.status === "done"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : optimizeState.status === "failed"
                  ? "border-red-200 bg-red-50 text-red-600"
                  : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              {optimizeIcon()}
              {optimizeLabel()}
            </button>
          )}
          {isOrphan && query.intent === "informational" && (
            <button
              onClick={() => onFaq(query)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
            >
              <ListChecks size={11} strokeWidth={2.5} />
              FAQ
            </button>
          )}
          {isOrphan && (
            <button
              onClick={() => onWrite(query)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              <PenLine size={11} strokeWidth={2.5} />
              Write
            </button>
          )}
          {!isOrphan && query.url && (
            <a
              href={query.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
            >
              <ExternalLink size={11} strokeWidth={2.5} />
              View
            </a>
          )}
          {!isOrphan && !isCannibalization && !isWrongPage && query.wp_post_id && (
            <button
              onClick={() => onFixCtr(query)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
            >
              <TrendingUp size={11} strokeWidth={2.5} />
              Fix CTR
            </button>
          )}
          {!isOrphan && !isCannibalization && !isWrongPage && (
            <button
              onClick={() => onWrite(query, true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              <PenLine size={11} strokeWidth={2.5} />
              Improve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function QueryGapsPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [jobState, setJobState] = useState({ status: "idle" });
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("orphan");
  const [intentFilter, setIntentFilter] = useState(null);
  const [error, setError] = useState(null);
  const [daysBack, setDaysBack] = useState(28);
  const [ctrFixQuery, setCtrFixQuery] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const res = await api.getQueryGaps();
      setJobState(res.state || { status: "idle" });
      setSummary(res.summary || null);
    } catch {
      setError("Failed to load gap analysis.");
    }
  }, []);

  const loadQueries = useCallback(async (gapType, intent) => {
    try {
      const params = { gap_type: gapType, limit: 200 };
      if (intent) params.intent = intent;
      const res = await api.listGscQueries(params);
      setQueries(res.queries || []);
    } catch {
      setError("Failed to load queries.");
    }
  }, []);

  useEffect(() => {
    loadSummary().finally(() => setLoading(false));
  }, [loadSummary]);

  useEffect(() => {
    if (summary) loadQueries(activeTab, intentFilter);
  }, [summary, activeTab, intentFilter, loadQueries]);

  // Poll while running
  useEffect(() => {
    if (jobState.status !== "running") return;
    const timer = setInterval(async () => {
      try {
        const res = await api.getQueryGaps();
        const newState = res.state || { status: "idle" };
        setJobState(newState);
        if (newState.status !== "running") {
          setSummary(res.summary || null);
          setRunning(false);
          clearInterval(timer);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(timer);
  }, [jobState.status]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await api.runQueryGapAnalysis(daysBack);
      setJobState(res.state || { status: "running" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start analysis.");
      setRunning(false);
    }
  };

  const handleWrite = async (query, isImprove = false) => {
    try {
      const reason = isImprove
        ? `Page ranks at position ${Number(query.position).toFixed(1)} for "${query.query}" (${query.impressions} impressions). Needs content improvement to break into top 10.`
        : `Real GSC query with ${query.impressions} impressions — no page currently ranks for it. Intent: ${query.intent}.`;
      const outline = await api.generateOutline({
        title: isImprove ? (query.article_title || query.query) : query.query,
        cluster: "",
        reason,
      });
      navigate(`/dashboard/writing/${outline.id}`);
    } catch {
      setError("Failed to generate outline.");
    }
  };

  const handleFaq = async (query) => {
    try {
      const outline = await api.generateOutline({
        title: query.query,
        cluster: "",
        reason: `Informational GSC query with ${query.impressions} impressions — no page ranks. Generate as FAQ format to capture featured snippet and answer searcher intent directly.`,
      });
      navigate(`/dashboard/writing/${outline.id}`);
    } catch {
      setError("Failed to generate FAQ outline.");
    }
  };

  const isRunning = jobState.status === "running" || running;

  // Count queries by intent for filter badges
  const intentCounts = queries.reduce((acc, q) => {
    acc[q.intent] = (acc[q.intent] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8 pb-6 border-b border-zinc-100">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Search Intelligence</p>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">Query Gaps</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Real GSC queries classified by intent — missing coverage and underperforming pages.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={daysBack}
              onChange={e => setDaysBack(Number(e.target.value))}
              disabled={isRunning}
              className="text-xs border border-zinc-200 rounded-lg px-3 py-2.5 text-zinc-700 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {PERIOD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={handleRun}
              disabled={isRunning}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              <RefreshCw size={13} strokeWidth={2.5} className={isRunning ? "animate-spin" : ""} />
              {isRunning ? "Analyzing…" : summary ? "Re-analyze" : "Run Analysis"}
            </button>
          </div>
        </div>
      </div>

      {/* Status banners */}
      {isRunning && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
          <RefreshCw size={13} strokeWidth={2} className="animate-spin shrink-0" />
          {jobState.message || "Fetching queries and classifying intent… this may take a few minutes."}
        </div>
      )}
      {jobState.status === "failed" && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
          <AlertCircle size={13} strokeWidth={2} />
          {jobState.message || "Analysis failed."}
        </div>
      )}
      {error && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
          <AlertCircle size={13} strokeWidth={2} />
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 text-zinc-400 text-sm">Loading…</div>
      )}

      {!loading && !summary && !isRunning && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
          <Search size={32} strokeWidth={1.5} className="text-zinc-300" />
          <p className="text-sm font-semibold text-zinc-600">No analysis yet</p>
          <p className="text-xs text-zinc-400 max-w-sm">
            Run analysis to fetch real GSC queries, classify intent, and surface content gaps.
          </p>
        </div>
      )}

      {!loading && summary && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Site Queries" value={summary.total_site_queries?.toLocaleString()} />
            <StatCard label="Missing Coverage" value={summary.orphan_count?.toLocaleString()} sub="no page ranks" />
            <StatCard label="Underperforming" value={summary.underperforming_count?.toLocaleString()} sub="position > 10" />
            <StatCard
              label="Last Analyzed"
              value={summary.analyzed_at ? new Date(summary.analyzed_at).toLocaleDateString() : "—"}
              sub={summary.analyzed_at ? `${summary.days_back ?? 28}d window · ${new Date(summary.analyzed_at).toLocaleTimeString()}` : ""}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-zinc-100 pb-2">
            {[
              { key: "orphan", label: "Missing Coverage", count: summary.orphan_stored ?? summary.orphan_count },
              { key: "underperforming", label: "Underperforming", count: summary.underperforming_stored ?? summary.underperforming_count },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); setIntentFilter(null); }}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${
                  activeTab === key ? "text-white" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                }`}
                style={activeTab === key ? { backgroundColor: PRIMARY } : {}}
              >
                {label}
                <span className={`ml-1.5 text-[10px] ${activeTab === key ? "opacity-75" : "text-zinc-400"}`}>
                  ({count ?? 0})
                </span>
              </button>
            ))}
          </div>

          {/* Intent filters */}
          <div className="flex flex-wrap gap-1.5 mb-5">
            <button
              onClick={() => setIntentFilter(null)}
              className={`px-3 py-1 text-[10px] font-bold rounded-full border transition-colors ${
                !intentFilter ? "text-white border-transparent" : "text-zinc-500 border-zinc-200 hover:bg-zinc-50"
              }`}
              style={!intentFilter ? { backgroundColor: PRIMARY } : {}}
            >
              All
            </button>
            {ALL_INTENTS.map(intent => {
              const c = INTENT_COLORS[intent];
              const active = intentFilter === intent;
              const cnt = intentCounts[intent] || 0;
              if (!cnt && !active) return null;
              return (
                <button
                  key={intent}
                  onClick={() => setIntentFilter(active ? null : intent)}
                  className="px-3 py-1 text-[10px] font-bold rounded-full border transition-all"
                  style={
                    active
                      ? { background: c.text, color: "#fff", borderColor: c.text }
                      : { background: c.bg, color: c.text, borderColor: "transparent" }
                  }
                >
                  {intent.replace(/_/g, " ")}{cnt ? ` (${cnt})` : ""}
                </button>
              );
            })}
          </div>

          {/* Query list */}
          {queries.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-zinc-400 gap-2">
              <CheckCircle size={24} strokeWidth={1.5} />
              <p className="text-sm">No queries in this category.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {queries.map((q) => (
                <QueryCard
                  key={q.id}
                  query={q}
                  onWrite={handleWrite}
                  onFaq={handleFaq}
                  onFixCtr={setCtrFixQuery}
                />
              ))}
            </div>
          )}

          {queries.length > 0 && (
            <p className="text-[10px] text-zinc-400 mt-4 text-center">
              Top {queries.length} by impressions · Last {summary?.days_back ?? 28} days GSC data
            </p>
          )}
        </>
      )}

      {ctrFixQuery && (
        <CtrFixModal query={ctrFixQuery} onClose={() => setCtrFixQuery(null)} />
      )}
    </div>
  );
}
